import type {
  FormFieldDescriptor,
  MappedFormState,
  NarrowFieldsByValueType,
  TidalFormState,
} from '@workwave-tidal/core/form'
import {
  Divider as BaseDivider,
  type DividerProps as BaseDividerProps,
} from '../components/Divider'
import {
  Fieldset as BaseFieldset,
  type FieldsetProps as BaseFieldsetProps,
} from '../components/Fieldset'
import { Form as BaseForm, type FormProps as BaseFormProps } from '../components/Form'
import {
  SectionSubtitle as BaseSectionSubtitle,
  type SectionSubtitleProps as BaseSectionSubtitleProps,
} from '../components/SectionSubtitle'
import {
  SectionTitle as BaseSectionTitle,
  type SectionTitleProps as BaseSectionTitleProps,
} from '../components/SectionTitle'
import {
  type AutocompleteValue,
  Autocomplete as BaseAutocomplete,
  type AutocompleteProps as BaseAutocompleteProps,
} from '../fields/Autocomplete'
import {
  BulkAutocomplete as BaseBulkAutocomplete,
  type BulkAutocompleteProps as BaseBulkAutocompleteProps,
  type BulkAutocompleteValue,
} from '../fields/Bulk/BulkAutocomplete'
import {
  BulkCheckbox as BaseBulkCheckbox,
  type BulkCheckboxProps as BaseBulkCheckboxProps,
  type BulkCheckboxValue,
} from '../fields/Bulk/BulkCheckbox'
import {
  BulkSelect as BaseBulkSelect,
  type BulkSelectProps as BaseBulkSelectProps,
  type BulkSelectValue,
} from '../fields/Bulk/BulkSelect'
import {
  BulkTagsField as BaseBulkTagsField,
  type BulkTagsFieldProps as BaseBulkTagsFieldProps,
  type BulkTagsFieldValue,
} from '../fields/Bulk/BulkTagsField'
import {
  BulkTextField as BaseBulkTextField,
  type BulkTextFieldProps as BaseBulkTextFieldProps,
  type BulkTextFieldValue,
} from '../fields/Bulk/BulkTextField'
import {
  ColorPicker as BaseColorPicker,
  type ColorPickerProps as BaseColorPickerProps,
  type ColorPickerValue,
} from '../fields/ColorPicker'
import {
  CurrencyInput as BaseCurrencyInput,
  type CurrencyInputProps as BaseCurrencyInputProps,
  type CurrencyInputValue,
} from '../fields/CurrencyInput'
import {
  DrawAreaField as BaseDrawAreaField,
  type DrawAreaFieldProps as BaseDrawAreaFieldProps,
  type DrawAreaFieldValue,
} from '../fields/DrawAreaField'
import {
  DrawRoadSegmentField as BaseDrawRoadSegmentField,
  type DrawRoadSegmentFieldProps as BaseDrawRoadSegmentFieldProps,
  type DrawRoadSegmentFieldValue,
} from '../fields/DrawRoadSegmentField'
import {
  LocationEditorField as BaseLocationEditorField,
  type LocationEditorFieldProps as BaseLocationEditorFieldProps,
  type LocationEditorFieldValue,
} from '../fields/LocationEditorField'
import {
  RecurrencePickerField as BaseRecurrencePickerField,
  type RecurrencePickerFieldProps as BaseRecurrencePickerFieldProps,
  type RecurrencePickerFieldValue,
} from '../fields/RecurrencePickerField'
import {
  TagsField as BaseTagsField,
  type TagsFieldProps as BaseTagsFieldProps,
  type TagsFieldValue,
} from '../fields/TagsField'
import {
  TimeWindowField as BaseTimeWindowField,
  type TimeWindowFieldProps as BaseTimeWindowFieldProps,
  type TimeWindowFieldValue,
} from '../fields/TimeWindowField'

import {
  type StandardPasswordValidationsOptions as BaseStandardPasswordValidationsOptions,
  useStandardPasswordValidations as useBaseStandardPasswordValidations,
} from '../hooks/useStandardPasswordValidations/useStandardPasswordValidations'

interface FormStateRmAdapter<State extends TidalFormState> {
  BulkSelect: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = BulkSelectValue,
  >(
    props: BaseBulkSelectProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  BulkCheckbox: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = BulkCheckboxValue,
  >(
    props: BaseBulkCheckboxProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  BulkTextField: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = BulkTextFieldValue,
  >(
    props: BaseBulkTextFieldProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  BulkAutocomplete: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = BulkAutocompleteValue,
  >(
    props: BaseBulkAutocompleteProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  ColorPicker: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = ColorPickerValue,
  >(
    props: BaseColorPickerProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  Autocomplete: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = AutocompleteValue,
  >(
    props: BaseAutocompleteProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  CurrencyInput: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = CurrencyInputValue,
  >(
    props: BaseCurrencyInputProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  LocationEditorField: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = LocationEditorFieldValue,
  >(
    props: BaseLocationEditorFieldProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  useStandardPasswordValidations: <
    TargetField extends NarrowFieldsByValueType<string, State, FormFieldDescriptor<State>>,
  >(
    fieldName: TargetField,
    options?: Partial<BaseStandardPasswordValidationsOptions>,
  ) => ReturnType<typeof useBaseStandardPasswordValidations<TargetField, State>>
  DrawRoadSegmentField: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = DrawRoadSegmentFieldValue,
  >(
    props: BaseDrawRoadSegmentFieldProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  Form: <State extends TidalFormState, ReturnedData = unknown>(
    props: BaseFormProps<State, ReturnedData>,
  ) => JSX.Element
  Fieldset: (props: BaseFieldsetProps) => JSX.Element
  Divider: (props: BaseDividerProps) => JSX.Element
  SectionSubtitle: (props: BaseSectionSubtitleProps) => JSX.Element
  SectionTitle: (props: BaseSectionTitleProps) => JSX.Element
  DrawAreaField: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = DrawAreaFieldValue,
  >(
    props: BaseDrawAreaFieldProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  RecurrencePickerField: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = RecurrencePickerFieldValue,
  >(
    props: BaseRecurrencePickerFieldProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  TimeWindowField: <
    TargetField extends NarrowFieldsByValueType<
      AcceptedFormValue,
      State,
      FormFieldDescriptor<State>
    >,
    AcceptedFormValue = TimeWindowFieldValue,
  >(
    props: BaseTimeWindowFieldProps<TargetField, State, AcceptedFormValue>,
  ) => JSX.Element
  TagsField: <
    TargetField extends NarrowFieldsByValueType<TagsFieldValue, State, FormFieldDescriptor<State>>,
  >(
    props: BaseTagsFieldProps<TargetField, State>,
  ) => JSX.Element
  BulkTagsField: <
    TargetField extends NarrowFieldsByValueType<
      BulkTagsFieldValue,
      State,
      FormFieldDescriptor<State>
    >,
  >(
    props: BaseBulkTagsFieldProps<TargetField, State>,
  ) => JSX.Element
}
export type RmAdapterMappedState<NS extends string, State extends TidalFormState> = MappedFormState<
  NS,
  FormStateRmAdapter<State>
>

export function setupFormStateRmAdapter<State extends TidalFormState>(): RmAdapterMappedState<
  '',
  State
> {
  // ---------------------------------------------------
  // ATTENTION: the type assertion won't enforce that the mapped keys are correct. Any key will be accepted.
  // The typed object will be correct, but the concrete implementation will not be enforced.
  // ---------------------------------------------------
  return {
    BulkSelect: function BulkSelect<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = BulkSelectValue,
    >(props: BaseBulkSelectProps<TargetField, State, AcceptedFormValue>) {
      return <BaseBulkSelect<TargetField, State, AcceptedFormValue> {...props} />
    },
    BulkCheckbox: function BulkCheckbox<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = BulkCheckboxValue,
    >(props: BaseBulkCheckboxProps<TargetField, State, AcceptedFormValue>) {
      return <BaseBulkCheckbox<TargetField, State, AcceptedFormValue> {...props} />
    },
    BulkAutocomplete: function BulkAutocomplete<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = BulkAutocompleteValue,
    >(props: BaseBulkAutocompleteProps<TargetField, State, AcceptedFormValue>) {
      return <BaseBulkAutocomplete<TargetField, State, AcceptedFormValue> {...props} />
    },
    BulkTextField: function BulkTextField<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = BulkTextFieldValue,
    >(props: BaseBulkTextFieldProps<TargetField, State, AcceptedFormValue>) {
      return <BaseBulkTextField<TargetField, State, AcceptedFormValue> {...props} />
    },
    ColorPicker: function ColorPicker<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = ColorPickerValue,
    >(props: BaseColorPickerProps<TargetField, State, AcceptedFormValue>) {
      return <BaseColorPicker<TargetField, State, AcceptedFormValue> {...props} />
    },
    CurrencyInput: function CurrencyInput<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = CurrencyInputValue,
    >(props: BaseCurrencyInputProps<TargetField, State, AcceptedFormValue>) {
      return <BaseCurrencyInput<TargetField, State, AcceptedFormValue> {...props} />
    },
    LocationEditorField: function LocationEditorField<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = LocationEditorFieldValue,
    >(props: BaseLocationEditorFieldProps<TargetField, State, AcceptedFormValue>) {
      return <BaseLocationEditorField<TargetField, State, AcceptedFormValue> {...props} />
    },
    DrawAreaField: function DrawAreaField<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = DrawAreaFieldValue,
    >(props: BaseDrawAreaFieldProps<TargetField, State, AcceptedFormValue>) {
      return <BaseDrawAreaField<TargetField, State, AcceptedFormValue> {...props} />
    },
    Autocomplete: function Autocomplete<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = AutocompleteValue,
    >(props: BaseAutocompleteProps<TargetField, State, AcceptedFormValue>) {
      return <BaseAutocomplete<TargetField, State, AcceptedFormValue> {...props} />
    },
    TagsField: function TagsField<
      TargetField extends NarrowFieldsByValueType<
        TagsFieldValue,
        State,
        FormFieldDescriptor<State>
      >,
    >(props: BaseTagsFieldProps<TargetField, State>) {
      return <BaseTagsField<TargetField, State> {...props} />
    },
    DrawRoadSegmentField: function DrawRoadSegmentField<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = DrawRoadSegmentFieldValue,
    >(props: BaseDrawRoadSegmentFieldProps<TargetField, State, AcceptedFormValue>) {
      return <BaseDrawRoadSegmentField<TargetField, State, AcceptedFormValue> {...props} />
    },
    Form: function Form<State extends TidalFormState, ReturnedData = unknown>(
      props: BaseFormProps<State, ReturnedData>,
    ) {
      return <BaseForm<State, ReturnedData> {...props} />
    },
    Fieldset: function Fieldset(props: BaseFieldsetProps) {
      return <BaseFieldset {...props} />
    },
    Divider: function Divider(props: BaseDividerProps) {
      return <BaseDivider {...props} />
    },
    SectionTitle: function SectionTitle(props: BaseSectionTitleProps) {
      return <BaseSectionTitle {...props} />
    },
    SectionSubtitle: function SectionTitle(props: BaseSectionSubtitleProps) {
      return <BaseSectionSubtitle {...props} />
    },
    useStandardPasswordValidations: function useStandardPasswordValidations<
      TargetField extends NarrowFieldsByValueType<string, State, FormFieldDescriptor<State>>,
    >(fieldName: TargetField, options?: Partial<BaseStandardPasswordValidationsOptions>) {
      return useBaseStandardPasswordValidations<TargetField, State>(fieldName, options)
    },
    RecurrencePickerField: function RecurrencePickerField<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = RecurrencePickerFieldValue,
    >(props: BaseRecurrencePickerFieldProps<TargetField, State, AcceptedFormValue>) {
      return <BaseRecurrencePickerField<TargetField, State, AcceptedFormValue> {...props} />
    },
    TimeWindowField: function TimeWindowField<
      TargetField extends NarrowFieldsByValueType<
        AcceptedFormValue,
        State,
        FormFieldDescriptor<State>
      >,
      AcceptedFormValue = TimeWindowFieldValue,
    >(props: BaseTimeWindowFieldProps<TargetField, State, AcceptedFormValue>) {
      return <BaseTimeWindowField<TargetField, State, AcceptedFormValue> {...props} />
    },
    BulkTagsField: function BulkTagsField<
      TargetField extends NarrowFieldsByValueType<
        BulkTagsFieldValue,
        State,
        FormFieldDescriptor<State>
      >,
    >(props: BaseBulkTagsFieldProps<TargetField, State>) {
      return <BaseBulkTagsField<TargetField, State> {...props} />
    },
  } as RmAdapterMappedState<'', State>
}
